/**
 * my-page「看板」タブ（external / 横断 PMO 向け）コンポーネント群。
 * my-page.tsx から分離（ファイル行数上限のため）。挙動は変更なし。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business my-page 看板タブの PL/主担当 担当案件テーブル一式
 * @graph-connects useMyProjectsBoard [calls] PL/主担当看板取得フック
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useMyProjectsBoard } from '../../../hooks/index.js';
import { indexDleTasksByParent, useMyDleTasks } from '../../../hooks/useDleEvaluationSurvey.js';
import { HealthBadge, HEALTH_COLORS, calcWeightedHealth } from '../health/index.js';
import { ProgressBar } from '../project/ProjectTable.js';
import { SlackChannelButton } from '../project/SlackChannelButton.js';
import { getStatusVariant } from '../project/ChildTicketsModal.js';
import { DevDesignViewSwitch, type BoardView } from './DevDesignViewSwitch.js';
import { MyDesignProjectsTable } from './MyDesignProjectsTable.js';
import { SurveyBatchAnswerButton } from './SurveyBatchAnswerButton.js';
import { DleSurveyBatchAnswerButton } from './DleSurveyBatchAnswerButton.js';
import { DleSurveyCell } from './DleSurveyCell.js';
import { filterIntegratedProducts, filterProjects } from './project-search.js';
import { orphanDleTasks } from './dle-survey-rows.js';
import { tokyoToday } from './settled-row-retention.js';
import type {
  PmoOverdueProject,
  MyProjectsBoardIntegratedProduct,
} from '../../../types/project.js';
import type { DleEvaluationTask } from '../../../types/dle-evaluation.js';

/** MyProjectsBoardTab の props。 @graph-connects none */
interface MyProjectsBoardTabProps {
  /** Slack DM の deep link で指定された子チケットキー（デザイナー貢献度アンケートの自動オープン用） */
  autoOpenChildIssueKey?: string;
  /** Slack DM の deep link で指定された母票キー（DLE 評価アンケートの自動オープン用） */
  autoOpenDleParentIssueKey?: string;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business my-page「看板」タブ用コンポーネント。ログインユーザーが PL (project_detail_table.assignee) または 主担当 (pb_sheet_sync.main_worker) になっている進行中Gen2案件を、統合プロダクト配下でチャンク折り畳み表示する
 * @graph-connects useMyProjectsBoard [calls] PL/主担当看板取得フック
 */
export function MyProjectsBoardTab({
  autoOpenChildIssueKey,
  autoOpenDleParentIssueKey,
}: MyProjectsBoardTabProps = {}) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useMyProjectsBoard();
  // 開発 / デザイン view 切替。両 view は同一データ（PL/主担当 抽出結果）を列だけ変えて表示する。
  // デザイナー貢献度アンケートの deep link で来たときだけ design view から開く
  // （その列は design view のテーブルにしか無いので、既定の dev view だと何も開かない）。
  // DLE 評価の deep link は dev view のままでよい。
  const [view, setView] = useState<BoardView>(
    autoOpenChildIssueKey === undefined ? 'dev' : 'design',
  );
  // 検索語は開発 / デザイン両 view で共通。view を切り替えても持ち越す。
  const [query, setQuery] = useState('');

  // dev view 用の案件（デザイン専用母票 = designOnly は dev view には出さない）。
  const devStandalone = data ? data.standaloneProjects.filter((p) => !p.designOnly) : [];
  const devProjects = data
    ? [...data.integratedProducts.flatMap((ip) => ip.chunkProjects), ...devStandalone]
    : [];
  // 担当案件（dev 由来）全体の平均健康度（計画pt加重）。
  const avgHealth = calcWeightedHealth(devProjects);
  const avgHealthColor = avgHealth ? HEALTH_COLORS[avgHealth.grade] : null;

  if (error) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-[var(--color-error)]">{t('feedback.errorMessage')}</p>
        <button
          type="button"
          onClick={() => {
            void refetch();
          }}
          className="text-sm text-[var(--color-accent-primary)] hover:underline"
        >
          {t('feedback.retry') ?? 'Retry'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data?.nicknameMissing && (
        <Card>
          <CardContent>
            <p className="py-3 text-sm text-[var(--color-warning)]">
              {t('myProjectsBoard.nicknameMissing')}
            </p>
          </CardContent>
        </Card>
      )}

      <Card
        className={`transition-colors duration-300 border-2 ${
          view === 'dev' ? 'border-[var(--color-dev-accent)]' : 'border-pink-300'
        }`}
      >
        {/*
          横 padding は Card 自身（padding="md" = 24px）が持つ。ここで px-6 を重ねると
          見出し行だけが左右 24px 内側に寄り、下のテーブルと縦のラインが揃わない。
        */}
        <div className="pt-5 pb-3 flex items-start justify-between gap-4">
          {/* 見出し列を flex-1 にして、一括回答ボタンを右端まで送れるようにする。 */}
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
              {t('myProjectsBoard.listTitle')}
            </h3>
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
              {t('myProjectsBoard.listSubtitle')}
            </p>
            <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5 opacity-80">
              {t('myProjectsBoard.listSubtitleHint')}
            </p>
            {/*
              一括回答ボタンは view 切替と同じ行の右端・底辺揃え。未回答が 2 件以上のときだけ出る。
              アンケート列は view ごとに別物（デザイン = デザイナー貢献度 / 開発 = DLE 評価）なので、
              ボタンも view に合わせて出し分ける。ただし deep link の自動オープンを担うため
              マウント自体はどちらも続ける（visible で出し分け）。
            */}
            <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-3">
                <DevDesignViewSwitch view={view} onChange={setView} />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                  }}
                  placeholder={t('myProjectsBoard.searchPlaceholder')}
                  aria-label={t('myProjectsBoard.searchPlaceholder')}
                  className="w-64 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-border-focus)] focus:outline-none"
                />
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <SurveyBatchAnswerButton
                  autoOpenChildIssueKey={autoOpenChildIssueKey}
                  visible={view === 'design'}
                />
                <DleSurveyBatchAnswerButton
                  autoOpenParentIssueKey={autoOpenDleParentIssueKey}
                  visible={view === 'dev'}
                />
              </div>
            </div>
          </div>
          {view === 'dev' && avgHealth && avgHealthColor && (
            <div className="flex flex-col items-center gap-1">
              <span
                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-lg font-bold"
                style={{ backgroundColor: avgHealthColor.bg, color: avgHealthColor.text }}
                title={`${t(avgHealthColor.labelKey)} (${String(avgHealth.score)}${t('projects.health.unit')})`}
              >
                {avgHealth.grade}
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                {t('myProjectsBoard.avgHealth')}
              </span>
              <span className="text-sm font-semibold text-[var(--color-text-secondary)]">
                {String(avgHealth.score)}
                {t('projects.health.unit')}
              </span>
            </div>
          )}
        </div>

        <CardContent>
          {isLoading ? (
            <div className="space-y-2 py-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
              ))}
            </div>
          ) : view === 'dev' ? (
            devProjects.length > 0 ? (
              <MyProjectsTable
                integratedProducts={data?.integratedProducts ?? []}
                standaloneProjects={devStandalone}
                query={query}
              />
            ) : (
              <p className="py-12 text-center text-sm text-[var(--color-text-muted)]">
                {t('myProjectsBoard.empty')}
              </p>
            )
          ) : (
            <MyDesignProjectsTable
              integratedProducts={data?.integratedProducts ?? []}
              standaloneProjects={data?.standaloneProjects ?? []}
              query={query}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface MyProjectsTableProps {
  integratedProducts: MyProjectsBoardIntegratedProduct[];
  standaloneProjects: PmoOverdueProject[];
  /** 案件キー / 案件名の検索語。空なら絞り込まない（未回答アンケートの行は検索中も常に出す） */
  query?: string;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 自分の担当案件テーブル。統合プロダクト配下のチャンクを折り畳み形式、スタンドアロンを通常行で表示し、案件一覧から落ちた完了母票の DLE 評価未回答を末尾に足す
 * @graph-connects useMyDleTasks [calls] DLE 評価タスク一覧取得
 * @graph-connects orphanDleTasks [calls] 行が描かれない未回答の拾い上げ
 * @graph-connects MyProjectRow [calls] 通常行描画
 * @graph-connects MyProjectsIntegratedRow [calls] 統合プロダクト折り畳み行描画
 */
export function MyProjectsTable({
  integratedProducts,
  standaloneProjects,
  query = '',
}: MyProjectsTableProps) {
  const { t } = useTranslation();
  // DLE 評価アンケートは担当案件とは別口径（開発完了以降の母票が対象）で取得する。
  // 担当案件テーブルは `完了` を除いた案件しか出さないため、行データからは導出できない。
  const { data: dleData } = useMyDleTasks();
  const dleTasks = useMemo(() => dleData?.tasks ?? [], [dleData]);
  const dleTaskByParent = indexDleTasksByParent(dleTasks);

  // **未回答は必ずどこかの行に出す**。開発 view は `完了` の母票を落とすが、DLE 評価は
  // `完了` も対象なので、落ちた案件こそ未回答が溜まりやすい。行が無い対象を末尾に足す。
  // 回答済みも 1 週間は残す（回答直後に行が消えると、回答が通ったことを確認できない）。
  const renderedParentKeys = new Set(
    [...standaloneProjects, ...integratedProducts.flatMap((ip) => ip.chunkProjects)].map(
      (p) => p.issueKey,
    ),
  );
  const orphanTasks = orphanDleTasks(dleTasks, renderedParentKeys, tokyoToday());

  // 検索は案件の行だけに効かせる。未回答アンケートの行（orphan）は絞り込まずに出す。
  const shownIntegrated = filterIntegratedProducts(query, integratedProducts);
  const shownStandalone = filterProjects(query, standaloneProjects);
  const noMatch =
    query.trim() !== '' &&
    shownIntegrated.length === 0 &&
    shownStandalone.length === 0 &&
    orphanTasks.length === 0;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-[var(--color-border)] text-[var(--color-text-muted)]">
            <th className="py-2 px-3 font-medium">{t('myProjectsBoard.columns.issueKey')}</th>
            <th className="py-2 px-3 font-medium">{t('myProjectsBoard.columns.summary')}</th>
            <th className="py-2 px-3 font-medium w-40">{t('myProjectsBoard.columns.progress')}</th>
            <th className="py-2 px-3 font-medium text-center">
              {t('myProjectsBoard.columns.health')}
            </th>
            <th className="py-2 px-3 font-medium">
              {t('myProjectsBoard.columns.systemTestDeadline')}
            </th>
            <th className="py-2 px-3 font-medium">{t('myProjectsBoard.columns.devLeader')}</th>
            <th className="py-2 px-3 font-medium">{t('myProjectsBoard.columns.dle')}</th>
            <th className="py-2 px-3 font-medium">{t('myProjectsBoard.columns.status')}</th>
            <th className="py-2 px-3 font-medium text-center">
              {t('myProjectsBoard.columns.survey')}
            </th>
            <th className="py-2 px-3 font-medium text-right">
              {t('myProjectsBoard.columns.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {shownIntegrated.map(({ ip, matchedChunks }) => (
            <MyProjectsIntegratedRow
              key={ip.integratedProductKey}
              ip={ip}
              dleTaskByParent={dleTaskByParent}
              forceExpanded={matchedChunks}
            />
          ))}
          {shownStandalone.map((p) => (
            <MyProjectRow
              key={p.issueKey}
              project={p}
              dleTask={dleTaskByParent.get(p.issueKey) ?? null}
            />
          ))}
          {orphanTasks.map((task) => (
            <DleSurveyOnlyRow key={task.parentIssueKey} task={task} />
          ))}
        </tbody>
      </table>
      {noMatch && (
        <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
          {t('myProjectsBoard.searchNoMatch')}
        </p>
      )}
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 案件一覧から落ちた母票（完了済み）の DLE 評価未回答行。通常の行と同じ形で末尾に出す
 * @graph-connects DleSurveyCell [calls] アンケート列セル描画
 */
function DleSurveyOnlyRow({ task }: { task: DleEvaluationTask }) {
  return (
    <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)] transition-colors">
      <td className="py-2 px-3 text-[var(--color-accent-primary)] whitespace-nowrap">
        <Link
          to="/projects/$pjKey"
          params={{ pjKey: task.parentIssueKey }}
          className="hover:underline"
        >
          {task.parentIssueKey}
        </Link>
      </td>
      <td className="py-2 px-3 max-w-md truncate" title={task.parentSummary}>
        {task.parentSummary}
      </td>
      {/*
        完了して一覧から落ちた母票。進捗 / 健康度 / 総合期限日 / 開発リーダー / Slack は
        案件一覧の行データが無いので出せない（アンケートを出すのが目的の行）。
        回答済みの行も 1 週間は残るので、見出しは回答状態に合わせて出し分ける。
      */}
      <td className="py-2 px-3 text-xs text-[var(--color-text-muted)]" colSpan={4}>
        {task.state === 'answered' ? '案件完了（DLE 評価 回答済）' : '案件完了（DLE 評価 未回答）'}
      </td>
      <td className="py-2 px-3 whitespace-nowrap">{task.dle}</td>
      <td className="py-2 px-3">
        <Badge variant={getStatusVariant(task.parentStatus)} size="sm" solid>
          {task.parentStatus}
        </Badge>
      </td>
      <td className="py-2 px-3 text-center whitespace-nowrap">
        <DleSurveyCell task={task} />
      </td>
      <td className="py-2 px-3" />
    </tr>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 統合プロダクトの折り畳み行。クリックで配下チャンクを展開
 * @graph-connects MyProjectRow [calls] 配下チャンク行描画
 */
function MyProjectsIntegratedRow({
  ip,
  dleTaskByParent,
  forceExpanded,
}: {
  ip: MyProjectsBoardIntegratedProduct;
  dleTaskByParent: Map<string, DleEvaluationTask>;
  /** 検索で配下チャンクが一致したときは自動で展開する */
  forceExpanded: boolean;
}) {
  const [toggled, setToggled] = useState(false);
  const expanded = toggled || forceExpanded;

  return (
    <>
      <tr
        className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors"
        onClick={() => {
          setToggled(!toggled);
        }}
      >
        <td className="py-2 px-3 text-[var(--color-accent-primary)] whitespace-nowrap">
          <span className="mr-1">{expanded ? '▼' : '▶'}</span>
          <Link
            to="/projects/$pjKey"
            params={{ pjKey: ip.integratedProductKey }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="hover:underline"
          >
            {ip.integratedProductKey}
          </Link>
        </td>
        <td
          className="py-2 px-3 max-w-md truncate font-medium text-[var(--color-text-primary)]"
          title={ip.summary}
          colSpan={9}
        >
          {ip.summary}
        </td>
      </tr>
      {expanded &&
        ip.chunkProjects.map((p) => (
          <MyProjectRow
            key={p.issueKey}
            project={p}
            dleTask={dleTaskByParent.get(p.issueKey) ?? null}
            indent
          />
        ))}
    </>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business my-page「看板」タブの1行表示。案件key・名前・進捗率・健康度・総合期限日・開発leader・DLE・ステータス・DLE評価アンケート・Slack channelボタンを表示
 * @graph-connects SlackChannelButton [calls] Slack channelボタン描画
 * @graph-connects DleSurveyCell [calls] DLE 評価アンケート列セル描画
 * @graph-connects ProgressBar [calls] 進捗率バー描画
 * @graph-connects HealthBadge [calls] 健康度バッジ描画
 */
function MyProjectRow({
  project,
  dleTask,
  indent = false,
}: {
  project: PmoOverdueProject;
  /** この母票の DLE 評価アンケート。対象ステータス前 / DLE 未解決なら null */
  dleTask: DleEvaluationTask | null;
  indent?: boolean;
}) {
  return (
    <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)] transition-colors">
      <td
        className={`py-2 px-3 text-[var(--color-accent-primary)] whitespace-nowrap ${
          indent ? 'pl-10' : ''
        }`}
      >
        <Link
          to="/projects/$pjKey"
          params={{ pjKey: project.issueKey }}
          className="hover:underline"
        >
          {project.issueKey}
        </Link>
      </td>
      <td className="py-2 px-3 max-w-md truncate" title={project.summary}>
        {project.summary}
      </td>
      <td className="py-2 px-3">
        <ProgressBar ratio={project.completionRatio} />
      </td>
      <td className="py-2 px-3 text-center">
        <HealthBadge health={project.health} />
      </td>
      <td className="py-2 px-3 whitespace-nowrap">
        {(project.systemTestDeadline ?? '').slice(0, 10) || '-'}
      </td>
      <td className="py-2 px-3 whitespace-nowrap">{project.devLeader ?? '-'}</td>
      {/*
        DLE は 要件定義レビュー 子票の実担当者（無ければ母票の開発リーダー）。開発リーダー列と
        同じ値になることもあるが、期中に DLE が交代した案件ではここだけが現任を指す。
      */}
      <td className="py-2 px-3 whitespace-nowrap">{project.dle ?? '-'}</td>
      <td className="py-2 px-3">
        <Badge variant={getStatusVariant(project.status)} size="sm" solid>
          {project.status}
        </Badge>
      </td>
      <td className="py-2 px-3 text-center whitespace-nowrap">
        <DleSurveyCell task={dleTask} />
      </td>
      <td className="py-2 px-3 text-right">
        <SlackChannelButton
          pjChannelId={project.pjSlackChannelId}
          devChannelId={project.devSlackChannelId}
        />
      </td>
    </tr>
  );
}
